/**
 * neuralNetPipeline — 120 firing neurons connected in a sparse dynamic
 * graph.
 *
 * Each neuron has a hash-derived position, a firing probability that
 * rises with the bass + mid, and an exponential afterglow envelope.
 * For every active neuron we draw a small Gaussian glow; for every
 * nearby-pair (within a base distance scaled by bass) we draw an
 * animated connecting line with a pulse traveling along the segment
 * proportional to the high band.
 *
 * The background uses a dark teal->navy radial gradient with a soft
 * FBM dust pattern so the scene is never pure black. The whole graph
 * drifts on a slow Lissajous so neurons are always in motion.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface NeuralNetPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  uResolution: WebGLUniformLocation | null;
  uTime: WebGLUniformLocation | null;
  uBass: WebGLUniformLocation | null;
  uMid: WebGLUniformLocation | null;
  uHigh: WebGLUniformLocation | null;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float;
layout(location = 0) in vec2 aPos;
out vec2 vUV;
void main() {
  vUV = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG_SRC = `#version 300 es
precision highp float;
in vec2 vUV;
out vec4 outColor;

uniform vec2 uResolution;
uniform float uTime;
uniform vec3 uBass;
uniform vec3 uMid;
uniform vec3 uHigh;

float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}
float noise2(vec2 p) {
  vec2 i = floor(p);
  vec2 f = fract(p);
  f = f * f * (3.0 - 2.0 * f);
  float a = hash21(i);
  float b = hash21(i + vec2(1.0, 0.0));
  float c = hash21(i + vec2(0.0, 1.0));
  float d = hash21(i + vec2(1.0, 1.0));
  return mix(mix(a, b, f.x), mix(c, d, f.x), f.y);
}
float fbm(vec2 p) {
  float v = 0.0, a = 0.5;
  for (int i = 0; i < 4; i++) {
    v += a * noise2(p);
    p *= 2.05; a *= 0.5;
  }
  return v;
}

float distToSeg(vec2 q, vec2 a, vec2 b) {
  vec2 ab = b - a;
  vec2 aq = q - a;
  float u = clamp(dot(aq, ab) / max(dot(ab, ab), 1e-5), 0.0, 1.0);
  return length(q - (a + ab * u));
}

// 48 neurons × 48 = 2304 O(N²) pairs per frame — well within budget.
#define NN_NODES 48
#define NN_MAX_PAIRS 2

void main() {
  vec2 p = (gl_FragCoord.xy - uResolution * 0.5) / min(uResolution.x, uResolution.y);
  p.x *= uResolution.x / uResolution.y;

  float bass = (uBass.x + uBass.y + uBass.z) / 3.0;
  float mid  = (uMid.x  + uMid.y  + uMid.z) / 3.0;
  float high = (uHigh.x + uHigh.y + uHigh.z) / 3.0;
  bass = pow(clamp(bass * 2.5, 0.0, 1.0), 0.7);
  mid  = pow(clamp(mid  * 2.5, 0.0, 1.0), 0.7);
  high = pow(clamp(high * 2.5, 0.0, 1.0), 0.7);

  float t = uTime;

  // Background: radial dark teal navy + FBM dust.
  float r2 = dot(p, p);
  vec3 bg = mix(vec3(0.020, 0.045, 0.070), vec3(0.005, 0.010, 0.020), r2);
  bg += vec3(0.025, 0.035, 0.045) * fbm(p * 2.0 + vec2(t * 0.05, -t * 0.03));
  vec3 col = bg;

  // Slow Lissajous drift.
  vec2 drift = vec2(sin(t * 0.18) * 0.10, cos(t * 0.13) * 0.06);

  // Compute node positions into a fixed-size local array.
  vec2 nodes[NN_NODES];
  float firePhase[NN_NODES];
  float fireEnvelope[NN_NODES];
  // Per-node base color seed.
  float nodeSeed[NN_NODES];

  for (int i = 0; i < NN_NODES; i++) {
    float fi = float(i);
    vec2 basePos = vec2(
      (hash21(vec2(fi, 11.0)) - 0.5) * 1.8,
      (hash21(vec2(fi, 13.0)) - 0.5) * 1.5
    ) * vec2(uResolution.x / uResolution.y, 1.0);
    // Slow per-node oscillation.
    vec2 osc = vec2(
      sin(t * (0.4 + hash21(vec2(fi, 19.0)) * 0.5) + fi * 1.7),
      cos(t * (0.3 + hash21(vec2(fi, 23.0)) * 0.4) + fi * 2.3)
    ) * 0.04;
    nodes[i] = basePos + drift + osc;
    firePhase[i] = hash21(vec2(fi, 29.0)) * 6.2832;
    float fr = hash21(vec2(fi, 31.0));
    // Firing envelope: firing intervals stagger across nodes.
    float period = 0.6 + hash21(vec2(fi, 37.0)) * 1.5;
    float cycle = mod(t + fr * 5.0, period) / period;
    fireEnvelope[i] = exp(-cycle * 5.0) * step(0.0, cycle);
    nodeSeed[i] = fr;
  }

  // Connections: each node finds its K nearest fire-active neighbors.
  // Per node we cap at NN_MAX_PAIRS. Walk nodes, for each find the
  // closest active neighbor and draw a segment with a traveling pulse.
  float maxDist = 0.18 + bass * 0.06;
  float baseLineWidth = 0.0025 + bass * 0.002;

  vec2 a, b;
  for (int i = 0; i < NN_NODES; i++) {
    // Skip inactive nodes entirely for connection drawing.
    if (fireEnvelope[i] < 0.02) continue;

    a = nodes[i];
    // Find up to 2 closest active nodes.
    float d1 = 9999.0; float d2 = 9999.0;
    int   i1 = -1;    int   i2 = -1;
    for (int j = 0; j < NN_NODES; j++) {
      if (j == i) continue;
      float ej = fireEnvelope[j];
      if (ej < 0.02) continue;
      b = nodes[j];
      float d = length(a - b);
      // Prefer closer & brighter — multiply dist by 1/ej to weight active.
      float weighted = d / max(0.05, ej * ej);
      if (weighted < d1) {
        d2 = d1; i2 = i1;
        d1 = weighted; i1 = j;
      } else if (weighted < d2) {
        d2 = weighted; i2 = j;
      }
      // Don't iterate more than we need.
    }
    if (i1 >= 0) {
      float realD = length(a - nodes[i1]);
      if (realD < maxDist) {
        float segD = distToSeg(p, a, nodes[i1]);
        float w = baseLineWidth;
        float lineMask = smoothstep(w * 1.6, w * 0.7, segD);
        // Pulse traveling from a -> b at high-bass speed.
        float speed = 0.7 + high * 0.8 + bass * 0.6;
        float pulseHead = fract(t * speed + firePhase[i] * 0.3
                              + hash21(vec2(float(i1), 41.0)) * 0.5);
        // Project p onto the segment to find u along the edge.
        vec2 abv = nodes[i1] - a;
        float u = clamp(dot(p - a, abv) / max(dot(abv, abv), 1e-5), 0.0, 1.0);
        float pulseDist = abs(u - pulseHead);
        float pulse = exp(-pulseDist * 20.0);
        vec3 edgeCol = mix(vec3(0.10, 0.50, 0.85),
                           vec3(0.85, 0.25, 0.85),
                           fract(firePhase[i] * 0.4));
        col += edgeCol * lineMask * (0.35 + pulse * 1.6) * fireEnvelope[i1];
      }
    }
    if (i2 >= 0) {
      float realD = length(a - nodes[i2]);
      if (realD < maxDist) {
        float segD = distToSeg(p, a, nodes[i2]);
        float w = baseLineWidth * 0.7;
        float lineMask = smoothstep(w * 1.6, w * 0.7, segD);
        float speed = 0.7 + high * 0.8 + bass * 0.6;
        float pulseHead = fract(t * speed * 1.2 + firePhase[i] * 0.5
                              + hash21(vec2(float(i2), 47.0)) * 0.5);
        vec2 abv = nodes[i2] - a;
        float u = clamp(dot(p - a, abv) / max(dot(abv, abv), 1e-5), 0.0, 1.0);
        float pulseDist = abs(u - pulseHead);
        float pulse = exp(-pulseDist * 20.0);
        vec3 edgeCol = mix(vec3(0.30, 0.85, 0.85),
                           vec3(0.85, 0.30, 0.30),
                           fract(firePhase[i] * 0.6));
        col += edgeCol * lineMask * (0.25 + pulse * 1.2) * fireEnvelope[i2];
      }
    }
  }

  // Node glows.
  for (int i = 0; i < NN_NODES; i++) {
    vec2 np = nodes[i];
    float d = length(p - np);
    float env = fireEnvelope[i];
    // Each node also has a small steady glow tied to its seed.
    float baseGlow = 0.15 + 0.25 * (0.5 + 0.5 * sin(t * 1.7 + firePhase[i]));
    // Color: cyan/magenta/yellow by seed.
    vec3 colA = vec3(0.10, 0.85, 1.00);
    vec3 colB = vec3(1.00, 0.25, 0.85);
    vec3 colC = vec3(1.00, 0.85, 0.30);
    vec3 nodeCol = mix(colA, colB, fract(nodeSeed[i] * 2.7));
    nodeCol = mix(nodeCol, colC, fract(nodeSeed[i] * 5.3));

    float core = exp(-d * 60.0) * (baseGlow + env * 2.5);
    float halo = exp(-d * 20.0) * (0.20 + env * 0.8);
    col += nodeCol * core;
    col += nodeCol * halo * 0.55;
    // Inactive nodes: tiny dim point + thin ring.
    float ringRadius = 0.007 + 0.001 * env;
    float ring = (1.0 - smoothstep(0.0, 0.0015, abs(d - ringRadius)))
               * (0.10 + env * 0.3 + mid * 0.20);
    col += nodeCol * ring;
  }

  // Overall audio-reactive tint.
  col += vec3(0.05, 0.15, 0.25) * bass * 0.35
       + vec3(0.20, 0.10, 0.30) * mid  * 0.25
       + vec3(0.15, 0.20, 0.05) * high * 0.15;

  // Vignette.
  col *= 1.0 - dot(p, p) * 0.30;

  // Tone map + gamma.
  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));
  outColor = vec4(col, 1.0);
}
`;

export function computeNeuralNetBands(t: number, out: Float32Array): void {
  out[0] = 0.5 + 0.4 * Math.sin(t * 0.7);
  out[1] = 0.6 + 0.4 * Math.abs(Math.sin(t * 1.4));
  out[2] = 0.4 + 0.3 * Math.sin(t * 2.2);
  out[3] = 0.5 + 0.4 * Math.sin(t * 1.0 + 1.0);
  out[4] = 0.5 + 0.4 * Math.sin(t * 1.6 + 2.3);
  out[5] = 0.4 + 0.4 * Math.sin(t * 2.3 + 3.1);
  out[6] = 0.3 + 0.5 * Math.sin(t * 3.7 + 0.5);
  out[7] = 0.4 + 0.4 * Math.sin(t * 5.4 + 1.7);
  out[8] = 0.3 + 0.5 * Math.sin(t * 7.4 + 2.1);
}

export function computeNeuralNetRealtimeBands(
  freqData: Uint8Array,
  out: Float32Array,
): void {
  const N = freqData.length;
  const positions = [0.005, 0.020, 0.050, 0.150, 0.300, 0.500, 0.650, 0.800, 0.950];
  for (let i = 0; i < 9; i++) {
    let sum = 0, count = 0;
    for (let k = -2; k <= 2; k++) {
      const b = Math.floor(positions[i] * N) + k;
      if (b >= 0 && b < N) { sum += freqData[b]; count++; }
    }
    out[i] = count > 0 ? (sum / count) / 255 : 0;
  }
}

export function createNeuralNetPipeline(gl: WebGL2RenderingContext): NeuralNetPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;

  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;

  const verts = new Float32Array([
    -1, -1,  1, -1,  -1, 1,
    -1,  1,  1, -1,   1, 1,
  ]);
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, verts, gl.STATIC_DRAW);
  gl.enableVertexAttribArray(POST_ATTRIBS.aPos);
  gl.vertexAttribPointer(POST_ATTRIBS.aPos, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  return {
    program, vao, vbo,
    uResolution: gl.getUniformLocation(program, "uResolution"),
    uTime: gl.getUniformLocation(program, "uTime"),
    uBass: gl.getUniformLocation(program, "uBass"),
    uMid: gl.getUniformLocation(program, "uMid"),
    uHigh: gl.getUniformLocation(program, "uHigh"),
    bands: new Float32Array(9),
  };
}

export function drawNeuralNet(
  gl: WebGL2RenderingContext,
  pipe: NeuralNetPipeline,
  width: number,
  height: number,
  t: number,
): void {
  gl.useProgram(pipe.program);
  gl.uniform2f(gl.getUniformLocation(pipe.program, "uResolution"), width, height);
  gl.uniform1f(gl.getUniformLocation(pipe.program, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uBass"), pipe.bands[0], pipe.bands[1], pipe.bands[2]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uMid"), pipe.bands[3], pipe.bands[4], pipe.bands[5]);
  gl.uniform3f(gl.getUniformLocation(pipe.program, "uHigh"), pipe.bands[6], pipe.bands[7], pipe.bands[8]);
  gl.bindVertexArray(pipe.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}
